import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Handshake, TrendingUp, AlertTriangle, Sparkles, 
  FileText, Send, CheckCircle2, Clock, Target
} from "lucide-react";
import { useDeals, Deal } from "@/hooks/useDeals";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { cn } from "@/lib/utils";

interface ClosingSignal {
  type: 'positive' | 'neutral' | 'negative';
  signal: string;
  confidence: number;
}

interface ClosingRecommendation {
  technique: string;
  script: string;
  timing: string;
}

export function AIClosingAssistant() {
  const { data: deals, isLoading: dealsLoading } = useDeals();
  const { isLoading: aiLoading, getDealInsights } = useAIAssistant();
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [closingAnalysis, setClosingAnalysis] = useState<{
    signals: ClosingSignal[];
    recommendations: ClosingRecommendation[];
    readiness_score: number;
  } | null>(null);

  // Get deals ready to close (high win probability, late stage)
  const closingReadyDeals = deals?.filter(d => 
    d.status === 'open' && (d.ai_win_probability || 0) >= 60
  ).sort((a, b) => (b.ai_win_probability || 0) - (a.ai_win_probability || 0)) || [];

  const analyzeClosingSignals = async (deal: any) => {
    setSelectedDeal(deal);
    
    // Generate AI analysis for closing signals
    const stageName = deal.pipeline_stages?.name || 'Unknown';
    const accountName = deal.accounts?.name || 'Unknown';
    const insights = await getDealInsights(
      deal,
      stageName,
      accountName
    );

    // Parse insights into structured closing data
    const signals: ClosingSignal[] = [
      { 
        type: (deal.ai_win_probability || 0) >= 70 ? 'positive' : 'neutral',
        signal: `Win probability at ${deal.ai_win_probability}%`,
        confidence: deal.ai_win_probability || 50
      },
      {
        type: deal.expected_close_date && new Date(deal.expected_close_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'positive' : 'neutral',
        signal: deal.expected_close_date ? `Expected close: ${new Date(deal.expected_close_date).toLocaleDateString()}` : 'No close date set',
        confidence: 70
      },
    ];

    const recommendations: ClosingRecommendation[] = [
      {
        technique: "Assumptive Close",
        script: `"Based on our discussions, it sounds like ${deal.title} is a great fit for your needs. Should we proceed with the paperwork to get you started by ${deal.expected_close_date ? new Date(deal.expected_close_date).toLocaleDateString() : 'next week'}?"`,
        timing: "Use when buyer has shown strong positive signals"
      },
      {
        technique: "Summary Close",
        script: `"Let me summarize what we've discussed: [recap key benefits]. Does this align with what you're looking to achieve?"`,
        timing: "Use to reinforce value before asking for commitment"
      },
      {
        technique: "Urgency Close",
        script: `"I want to make sure you can take advantage of [current offer/pricing]. This is available until [date]. Shall we move forward?"`,
        timing: "Use when there's a genuine time constraint"
      },
    ];

    setClosingAnalysis({
      signals,
      recommendations,
      readiness_score: deal.ai_win_probability || 50,
    });
  };

  const getSignalIcon = (type: string) => {
    switch (type) {
      case 'positive':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'negative':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Handshake className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">AI Closing Assistant</h2>
          <p className="text-sm text-muted-foreground">
            Detect closing signals and get AI-powered closing scripts
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deals Ready to Close */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-green-500" />
              Deals Ready to Close
            </CardTitle>
            <CardDescription>
              High-probability deals detected by AI
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dealsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : closingReadyDeals.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {closingReadyDeals.map(deal => (
                  <div 
                    key={deal.id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all hover:border-primary",
                      selectedDeal?.id === deal.id && "border-primary bg-primary/5"
                    )}
                    onClick={() => analyzeClosingSignals(deal)}
                  >
                    <div>
                      <div className="font-medium">{deal.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {(deal as any).accounts?.name} • ${deal.amount?.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={(deal.ai_win_probability || 0) >= 70 ? "default" : "secondary"}
                        className={cn(
                          (deal.ai_win_probability || 0) >= 70 && "bg-green-500"
                        )}
                      >
                        {deal.ai_win_probability}% Win
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No deals ready for closing yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Closing Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Closing Analysis
            </CardTitle>
            <CardDescription>
              {selectedDeal ? selectedDeal.title : "Select a deal to analyze"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedDeal ? (
              <div className="text-center py-8 text-muted-foreground">
                <Handshake className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Select a deal to see closing signals and recommendations</p>
              </div>
            ) : aiLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : closingAnalysis ? (
              <div className="space-y-4">
                {/* Readiness Score */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Closing Readiness</span>
                    <span className={cn(
                      "text-lg font-bold",
                      closingAnalysis.readiness_score >= 70 ? "text-green-500" :
                      closingAnalysis.readiness_score >= 50 ? "text-amber-500" : "text-red-500"
                    )}>
                      {closingAnalysis.readiness_score}%
                    </span>
                  </div>
                  <Progress value={closingAnalysis.readiness_score} className="h-2" />
                </div>

                {/* Signals */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Detected Signals</h4>
                  <div className="space-y-2">
                    {closingAnalysis.signals.map((signal, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {getSignalIcon(signal.type)}
                        <span>{signal.signal}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Closing Techniques</h4>
                  <div className="space-y-3">
                    {closingAnalysis.recommendations.map((rec, i) => (
                      <div key={i} className="p-3 rounded-lg border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{rec.technique}</span>
                          <Badge variant="outline" className="text-xs">{rec.timing}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground italic">"{rec.script}"</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" className="flex-1">
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Contract
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1">
                    <Send className="h-4 w-4 mr-2" />
                    Send Proposal
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
